import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Geocoder from '../../components/LocationAutocompleteInput/GeocoderGoogleMaps';
import defaultCitiesList from './default-cities';
import { decodeLatLng, decodeLatLngBounds } from '../../util/urlHelpers';
import css from './MainFiltersForm.css';

const TOTAL_NUMBER_OF_PREDICTION = 5;
const TOTAL_NUMBER_OF_SUGGESTIONS = 6;

const unique = (arr, keyProps) => {
  return Object.values(
    arr.reduce((uniqueMap, entry) => {
      const key = keyProps.map(k => entry[k]).join('|');
      if (!(key in uniqueMap)) uniqueMap[key] = entry;
      return uniqueMap;
    }, {})
  );
};

const LocationSelector = ({
  searchQuery = '',
  latestSearches = [],
  placeholder = '',
  onInputChange,
  onClear,
  onSelect,
  debounceTimeout = 300,
  setActiveSection,
  handleClearRecentWhere,
}) => {
  const [inputValue, setInputValue] = useState(searchQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [predictions, setPredictions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const geocoder = useMemo(() => new Geocoder(), []);
  const requestCount = useRef(0);

  const uniqueLatestSearches = useMemo(() => {
    return unique(
      (latestSearches || []).filter(item => !!item.location),
      ['location']
    ).slice(0, 3);
  }, [latestSearches]);

  const countryCities = useMemo(() => {
    const remainingSlots = TOTAL_NUMBER_OF_SUGGESTIONS - uniqueLatestSearches.length;
    if (remainingSlots <= 0) return [];

    return defaultCitiesList
      .filter(city => city.countryId === process.env.REACT_APP_COUNTRY_ID)
      .slice(0, remainingSlots);
  }, [uniqueLatestSearches.length]);

  const flatList = useMemo(() => {
    if (debouncedQuery.length > 0) {
      return predictions.map(p => ({ item: p, isPrediction: true }));
    } else {
      return [
        ...uniqueLatestSearches.map(l => ({ item: l, isPrediction: false })),
        ...countryCities.map(c => ({ item: c, isPrediction: false })),
      ];
    }
  }, [debouncedQuery, predictions, uniqueLatestSearches, countryCities]);

  const handleSelectItem = useCallback(
    async wrapper => {
      if (!wrapper) return;
      const { item, isPrediction } = wrapper;
     
      try {
        if (isPrediction) {
          const placeDetails = await geocoder.getPlaceDetails(item);
          onSelect({
            location: placeDetails.address,
            origin: placeDetails.origin,
            bounds: placeDetails.bounds,
          });
        } else {
          onSelect({
            location: item.location,
            origin: decodeLatLng(item.origin),
            bounds: decodeLatLngBounds(item.bounds),
          });
        }
      } catch (e) {
        console.error('Błąd wyboru lokalizacji:', e);
      }
    },
    [geocoder, onSelect]
  );

  const handleKeyDown = e => {
    if (!flatList || flatList.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < flatList.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : flatList.length - 1));
    } else if (e.key === 'Escape') {
      setFocusedIndex(-1);
      inputRef.current && inputRef.current.blur();
      setActiveSection(null);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (focusedIndex >= 0) {
        !!flatList[focusedIndex] && handleSelectItem(flatList[focusedIndex]);
      } else if (focusedIndex === -1 && inputValue.length > 0) {
        !!flatList[0] && handleSelectItem(flatList[0]);
      } else {
        inputRef.current && inputRef.current.blur();
        setActiveSection(null);
      }
    }
  };

  const handleClearClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setInputValue('');
    setDebouncedQuery('');
    if (onClear) onClear(e);
  };

  useEffect(() => {
    setInputValue(searchQuery);
    setDebouncedQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(inputValue);
      if (onInputChange) {
        onInputChange(inputValue);
      }
    }, debounceTimeout);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, debounceTimeout, onInputChange]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    inputValue.length > 0 ? setFocusedIndex(0) : setFocusedIndex(-1);
  }, [debouncedQuery]);

  useEffect(() => {
    if (debouncedQuery && debouncedQuery.length > 1) {
      setFetching(true);
      const currentRequest = ++requestCount.current;

      geocoder
        .getPlacePredictions(debouncedQuery, { types: ['(cities)'] })
        .then(results => {
          if (currentRequest === requestCount.current) {
            const limitedPredictions = (results.predictions || []).slice(
              0,
              TOTAL_NUMBER_OF_PREDICTION
            );
            setPredictions(limitedPredictions);
            setFetching(false);
          }
        })
        .catch(() => {
          if (currentRequest === requestCount.current) setFetching(false);
        });
    } else {
      setPredictions([]);
      setFetching(false);
    }
  }, [debouncedQuery, geocoder]);

  return (
    <>
      <div className={css.inputWrapper}>
        <input
          ref={inputRef}
          className={css.invisibleInput}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
        />
        {inputValue && (
          <button type="button" className={css.clearBtn} onClick={handleClearClick}>
            <span className={css.clearIcon}>×</span>
          </button>
        )}
      </div>

      <div
        className={classNames(css.dropdown, css.locationDropdown)}
        onClick={e => e.stopPropagation()}
      >
        <div className={css.scrollContainer}>
          {fetching && <div className={css.loadingBar} />}
          {debouncedQuery.length > 0 ? (
            <div className={css.resultsWrapper}>
              <div className={css.dropdownHeader}>
                <FormattedMessage id="MarketplaceSearchBar.searchResults" />
              </div>
              {predictions.length > 0 ? (
                predictions.map((p, i) => (
                  <div
                    key={`p-${i}`}
                    className={classNames(css.dropItem, { [css.focusedItem]: i === focusedIndex })}
                    onClick={() => handleSelectItem({ item: p, isPrediction: true })}
                  >
                    <span className={css.itemLabel}>{p.description}</span>
                  </div>
                ))
              ) : !fetching ? (
                <div className={css.noResults}>
                  <FormattedMessage id="MarketplaceSearchBar.noResults" />
                </div>
              ) : null}
            </div>
          ) : (
            <div className={css.defaultWrapper}>
              {uniqueLatestSearches.length > 0 && (
                <div className={css.sectionGroup}>
                  <div className={css.dropdownHeaderContainer}>
                    <div className={css.dropdownHeader}>
                      <FormattedMessage id="MainFilters.latestSearch" />
                    </div>
                    <button
                      type="button"
                      className={css.clearRecentBtn}
                      onClick={handleClearRecentWhere}
                    >
                      <span className={css.clearIcon}>×</span>
                    </button>
                  </div>
                  {uniqueLatestSearches.map((item, i) => (
                    <div
                      key={`l-${i}`}
                      className={classNames(css.dropItem, {
                        [css.focusedItem]: i === focusedIndex,
                      })}
                      onClick={() => handleSelectItem({ item, isPrediction: false })}
                    >
                      <span className={css.itemLabel}>{item.location}</span>
                    </div>
                  ))}
                </div>
              )}
              {countryCities.length > 0 && (
                <div className={css.sectionGroup}>
                  <div className={css.dropdownHeader}>
                    <FormattedMessage id="MarketplaceSearchBar.popularCities" />
                  </div>
                  {countryCities.map((item, i) => {
                    const globalIndex = i + uniqueLatestSearches.length;
                    return (
                      <div
                        key={`c-${i}`}
                        className={classNames(css.dropItem, {
                          [css.focusedItem]: globalIndex === focusedIndex,
                        })}
                        onClick={() => handleSelectItem({ item, isPrediction: false })}
                      >
                        <span className={css.itemLabel}>{item.location}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LocationSelector;
